import { Application, DisplayObject } from '@tencent/alloy-renderer';
import { BrowserTarget } from '@tencent/alloy-renderer-browser';
import { RenderArea } from '@src/slide/content-viewer/service/canvas-renderer/entry/base/performance/layered-render/render-area';
import { IRenderRectConfig } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-mobile/type';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';

export interface RegionRenderConfig {
    width: number,
    height: number,
    scale: number,
    renderRectConfig?: IRenderRectConfig;
}

export interface RegionRenderProps extends RegionRenderConfig {
    canvasElement: HTMLCanvasElement,
}

export default class RegionRenderService {
    protected application: Application;
    protected renderArea: RenderArea;
    protected canvasElement: HTMLCanvasElement;
    protected scale: number;
    protected width: number;
    protected height: number;
    protected left: number;
    protected top: number;
    protected currentScrollX: number;
    protected currentScrollY: number;
    protected renderRectConfig: IRenderRectConfig | undefined;

    public constructor(props: RegionRenderProps) {
        const { scale, width, height, canvasElement, renderRectConfig } = props;
        this.scale = scale;
        this.width = width;
        this.height = height;
        const { slideLeft, slideTop } = this.getAppServices()
            .viewableServiceLayer.editAreaCanvasSizeManager.getCanvasSize();
        this.left = slideLeft;
        this.top = slideTop;
        this.canvasElement = canvasElement;
        this.renderRectConfig = renderRectConfig;

        const target = new BrowserTarget();
        this.application = new Application(target, {
            native: canvasElement,
            interactive: false,
        });
        this.application.resolution = Math.ceil(window.devicePixelRatio);
        this.setScale(scale);
        this.renderArea = new RenderArea(
            this.width,
            this.height,
            this.scale,
            this.application,
            renderRectConfig,
        );
        this.init();
    }

    public getApplication(): Application {
        return this.application;
    }

    public getRenderArea(): RenderArea {
        return this.renderArea;
    }

    public init(): void {
        // 子类自行实现
    }

    public dispose(): void {
        // 子类自行实现
    }

    /**
     * 添加元素
     */
    public updateComponents(component: DisplayObject): void {
        this.renderArea.updateComponents(component);
    }

    /**
     * 清理元素
     * @param component
     */
    public clearComponents(component: DisplayObject): void {
        this.renderArea.clearComponent(component);
    }

    /**
     * 清理全部元素
     * @param component
     */
    public clearAllComponent(): void {
        this.renderArea.clearAllComponent();
    }

    public setScale(scale: number): void {
        const width = this.getAppServices().getUiDataService().presentation.props.slideWidth * scale;
        const height = this.getAppServices().getUiDataService().presentation.props.slideHeight * scale;
        this.application.width = width;
        this.application.height = height;
        this.application.stage.transform.scaleX = scale;
        this.application.stage.transform.scaleY = scale;
        this.application.requestRender();
    }

    public updateWithData(config: RegionRenderConfig): void {
        const { width, height, scale, renderRectConfig } = config;
        const { slideLeft, slideTop } = this.getAppServices()
            .viewableServiceLayer.editAreaCanvasSizeManager.getCanvasSize();
        const { currentScrollX = 0, currentScrollY = 0, contentSize = {} } = renderRectConfig || {};
        const diffProps = Object.keys(contentSize)
            .filter(key => contentSize[key] !== this.renderRectConfig?.contentSize?.[key]);
        if (
            this.width === width
            && this.height === height
            && this.scale === scale
            && this.left === slideLeft
            && this.top === slideTop
            && this.currentScrollX === currentScrollX
            && this.currentScrollY === currentScrollY
            && diffProps.length === 0
        ) return;
        this.width = width;
        this.height = height;
        this.left = slideLeft;
        this.top = slideTop;
        this.scale = scale;
        this.currentScrollX = currentScrollX;
        this.currentScrollY = currentScrollY;
        this.renderRectConfig = renderRectConfig;
        this.renderArea.updateInfo(width, height, scale, renderRectConfig);
    }

    public updatePosition(renderRectConfig?: IRenderRectConfig): void {
        this.renderArea.updatePosition(renderRectConfig);
    }

    protected getAppServices(): IEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as
            IEditorAppServices;
    }
}
